import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import Layout from '../../layout/Layout';
import { useNavigate, useLocation } from 'react-router-dom';
import backButtonIcon from '../../../assets/backButton.png';
import SkuImage from '../../common/SkuImage';
import Pagination from '../../common/Pagination';
import LoadingSpinner from '../../ui/LoadingSpinner';
import supplierService from '../../../services/supplierService';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../common/Toast';

// Interface for dashboard product data
interface DashboardProduct {
  id: string;
  code: string;
  image_path?: string;
  description: string;
  styleCode: string;
  rrp: string;
  bwaBuyPrice: string;
  regionalRetailAUD: string;
  regionalTradeAUD: string;
  metroRetailAUD: string;
  metroTradeAUD?: string;
  regionalRetailVIP?: string;
  regionalTradeVIP1?: string;
  regionalTradeVIP2?: string;
  metroRetailVIP?: string;
  metroTradeVIP1?: string;
  metroTradeVIP2?: string;
  supplierName: string;
  supplierId: number;
}

const PageContainer = styled.div`
  background: white;
  margin: 0;
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  padding: 0 24px;
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  /* Enable proper horizontal scrolling on all screen sizes */
  @media (max-width: 1200px) {
    padding: 0 16px;
  }
  
  @media (max-width: 768px) {
    padding: 0 12px;
  }
  
  @media (max-height: 600px) {
    min-height: calc(100vh - 60px);
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
    gap: 6px;
  }
  
  @media (max-height: 600px) {
    margin-bottom: 0.75rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #042B41;
  border-radius: 4px;
  
  &:hover {
    background-color: #f3f4f6;
  }
  
  @media (max-width: 480px) {
    padding: 4px;
  }
`;

const BackIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 8px;
  
  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
    margin-right: 4px;
  }
`;

const PageTitle = styled.h1`
  color: #042B41;
  font-size: clamp(20px, 4vw, 32px);
  font-weight: 800;
  margin: 0;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: clamp(18px, 5vw, 26px);
  }
  
  @media (max-width: 480px) {
    font-size: clamp(16px, 6vw, 22px);
  }
  
  @media (max-height: 600px) {
    font-size: clamp(16px, 4vw, 24px);
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: auto !important;
  overflow-y: visible;
  margin-bottom: 1rem;
  border: 1px solid #e0e0e0;
  position: relative;
  flex: 1;
  min-height: 400px;
  
  /* Force horizontal scrolling */
  min-width: 0;
  width: 100%;
  
  /* Enhanced scrollbar for better visibility */
  &::-webkit-scrollbar {
    height: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(90deg, #888, #666);
    border-radius: 6px;
    border: 1px solid #ccc;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(90deg, #666, #444);
  }
  
  /* Ensure scrolling works on all devices */
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
  
  @media (max-width: 768px) {
    border-radius: 6px;
    min-height: 300px;
  }
  
  @media (max-width: 480px) {
    border-radius: 4px;
    min-height: 250px;
  }
  
  @media (max-height: 600px) {
    min-height: 200px;
  }
`;

const ScrollableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: visible; /* Changed to visible so table expands to fit content */
  width: 100%;
  height: auto; /* Auto height to fit content exactly */
  
  /* Custom scrollbar styling for both horizontal and vertical scrollbars */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  
  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1500px; /* Increased to accommodate SKU Image column */
  border-collapse: collapse;
  font-size: 14px;
  
  @media (max-width: 768px) {
    font-size: 12px;
    min-width: 1200px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
    min-width: 1000px;
  }
  
  @media (max-height: 600px) {
    font-size: 12px;
    min-width: 1200px;
  }
`;

const TableHeader = styled.thead`
  background-color: #042B41;
  color: white;
  font-family: 'Avenir', sans-serif;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeaderRow = styled.tr`
  border: none;
`;

const TableHeaderCell = styled.th`
  padding: clamp(4px, 0.8vw, 6px) clamp(6px, 1vw, 8px);
  text-align: left;
  font-weight: 500;
  color: white;
  border-right: 1px solid #0A3D5A;
  border-left: 1px solid #0A3D5A;
  white-space: nowrap;
  min-width: 140px;
  height: clamp(32px, 4vw, 36px);
  vertical-align: middle;
  font-size: clamp(11px, 1.4vw, 12px);
  line-height: 1.2;
  box-sizing: border-box;
  transition: font-size 0.2s ease;
  
  &:last-child {
    border-right: none;
  }
  
  &:first-child {
    min-width: 120px;
  }
  
  &:nth-child(2) {
    min-width: 80px; /* SKU Image column */
  }
  
  &:nth-child(3) {
    min-width: 280px;
    text-align: left;
  }
  
  &:nth-child(4) {
    min-width: 160px;
  }
  
  /* Progressive scaling for different zoom levels */
  @media (min-resolution: 144dpi) {
    font-size: clamp(10px, 1.3vw, 11px);
    padding: clamp(3px, 0.7vw, 5px) clamp(5px, 0.9vw, 7px);
  }
  
  @media (max-width: 768px) {
    padding: 3px 5px;
    font-size: clamp(10px, 1.2vw, 11px);
    min-width: 100px;
    height: clamp(28px, 3.5vw, 32px);
    
    &:nth-child(2) {
      min-width: 60px;
    }
    
    &:nth-child(3) {
      min-width: 200px;
    }
    
    &:nth-child(4) {
      min-width: 120px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 3px 5px;
    font-size: clamp(10px, 1.2vw, 11px);
    min-width: 80px;
    
    &:nth-child(2) {
      min-width: 50px;
    }
    
    &:nth-child(3) {
      min-width: 180px;
    }
    
    &:nth-child(4) {
      min-width: 100px;
    }
  }
  
  @media (max-height: 600px) {
    padding: clamp(3px, 0.7vw, 5px) clamp(5px, 0.9vw, 7px);
    font-size: clamp(10px, 1.3vw, 11px);
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;
  min-height: clamp(36px, 4.5vw, 40px);
  
  &:hover {
    background-color: #F9FAFB;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: clamp(4px, 0.8vw, 6px) clamp(6px, 1vw, 8px);
  text-align: left;
  color: #333;
  border: 1px solid #e0e0e0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  font-size: clamp(11px, 1.4vw, 12px);
  box-sizing: border-box;
  transition: font-size 0.2s ease;
  
  &.wrap {
    white-space: normal;
    word-break: break-word;
    overflow: visible;
    text-overflow: unset;
  }
  
  &:last-child {
    border-right: none;
  }
  
  &:first-child {
    font-weight: 600;
    color: #042B41;
  }
  
  &:nth-child(2) {
    padding: 8px; /* Reduced padding for image column */
    text-align: center;
  }
  
  &:nth-child(3) {
    text-align: left;
    max-width: 280px;
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.4;
    overflow: visible;
    text-overflow: unset;
  }
  
  &:nth-child(4) {
    font-size: clamp(10px, 1.3vw, 11px);
    color: #6B7280;
  }
  
  /* Progressive scaling for different zoom levels */
  @media (min-resolution: 144dpi) {
    font-size: clamp(10px, 1.3vw, 11px);
    padding: clamp(3px, 0.7vw, 5px) clamp(5px, 0.9vw, 7px);
  }
  
  @media (max-width: 768px) {
    padding: 3px 5px;
    font-size: clamp(10px, 1.2vw, 11px);
    
    &:nth-child(2) {
      padding: 4px; /* Even smaller padding for mobile */
    }
    
    &:nth-child(3) {
      max-width: 200px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 3px 5px;
    font-size: clamp(10px, 1.2vw, 11px);
    
    &:nth-child(2) {
      padding: 2px; /* Even smaller padding for mobile */
    }
    
    &:nth-child(3) {
      max-width: 180px;
      font-size: clamp(9px, 1.1vw, 10px);
    }
    
    &:nth-child(4) {
      font-size: clamp(9px, 1.1vw, 10px);
    }
  }
  
  @media (max-height: 600px) {
    padding: clamp(3px, 0.7vw, 5px) clamp(5px, 0.9vw, 7px);
    font-size: clamp(10px, 1.3vw, 11px);
  }
`;

const PriceCell = styled(TableCell)`
  font-weight: 500;
  text-align: right;
  color: #333;
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
  
  @media (max-height: 600px) {
    margin-bottom: 0.5rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex-grow: 1;
  max-width: 400px;
  min-width: 200px;
  
  @media (max-width: 768px) {
    max-width: 100%;
    order: 1;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: clamp(8px, 1.2vw, 10px) clamp(12px, 1.5vw, 16px) clamp(8px, 1.2vw, 10px) clamp(36px, 4vw, 40px);
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: clamp(13px, 1.6vw, 14px);
  font-weight: 400;
  height: clamp(36px, 4.5vw, 40px);
  box-sizing: border-box;
  background-color: #FAFAFA;
  min-height: 44px; /* Touch target */
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z'/%3e%3c/svg%3e");
  background-position: left 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1rem 1rem;
  
  &::placeholder {
    color: #9CA3AF;
    font-size: clamp(13px, 1.6vw, 14px);
  }
  
  &:focus {
    outline: none;
    border-color: #042B41;
    background-color: white;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
  
  @media (max-width: 768px) {
    height: 48px;
    padding: 12px 16px 12px 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 16px; /* Prevents zoom on mobile */
    height: 52px;
    padding: 14px 18px 14px 3.5rem;
  }
  
  @media (max-height: 600px) {
    height: 40px;
    padding: 8px 12px 8px 2.5rem;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: 14px;
  color: #6B7280;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    order: 2;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const StatItem = styled.div`
  font-weight: 500;
  
  strong {
    color: #111827;
    font-weight: 700;
  }
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #042B41;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  box-sizing: border-box;
  white-space: nowrap;
  
  &:hover {
    background-color: #053c59;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 13px;
    gap: 6px;
  }
  
  @media (max-height: 600px) {
    padding: 8px 12px;
    font-size: 12px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6B7280;
  
  h3 {
    margin: 0 0 0.5rem;
    color: #111827;
    font-size: 18px;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
`;

const ScrollHint = styled.div`
  background-color: #F9FAFB;
  border-top: 1px solid #E5E7EB;
  padding: 8px 16px;
  text-align: center;
  font-size: 12px;
  color: #6B7280;
  
  @media (min-width: 1600px) {
    display: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  width: 100%;
`;

const LoadingText = styled.p`
  margin: 0;
  color: #6B7280;
  font-size: 16px;
`;

const PaginationWrapper = styled.div`
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
`;

const SuppliersDashboard: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  
  // Initialize state from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const initialPage = parseInt(urlParams.get('page') || '1', 10);
  const initialLimit = parseInt(urlParams.get('limit') || '5', 10);
  const initialSearch = urlParams.get('search') || '';

  // State management
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [pagination, setPagination] = useState({ currentPage: initialPage, itemsPerPage: initialLimit });
  const { currentPage, itemsPerPage } = pagination;
  const [dashboardData, setDashboardData] = useState<DashboardProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Refs for preventing duplicate API calls
  const apiCallInProgress = useRef(false);
  const hasMounted = useRef(false);
  const hasInitializedSearch = useRef(false);

  // Function to update URL with current pagination state
  const updateURLParams = useCallback((page: number, limit: number, search: string) => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    if (search.trim()) {
      params.set('search', search.trim());
    }
    
    const newUrl = `${location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }, [location.pathname]);

  // Function to fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (apiCallInProgress.current) {
      return;
    }

    apiCallInProgress.current = true;
    setLoading(true);
    
    try {
      console.log('SuppliersDashboard - Fetching data with params:', {
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery
      });

      // Build search parameters for the optimized dashboard endpoint
      const searchParams = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        ...(searchQuery.trim() && { search: searchQuery.trim() })
      };

      // Use the optimized dashboard endpoint
      const response = await supplierService.getDashboardData(searchParams);
      
      console.log('SuppliersDashboard - Dashboard API response:', response);

      if (!response.items || !Array.isArray(response.items)) {
        console.warn('SuppliersDashboard - No items in dashboard API response');
        setDashboardData([]);
        setTotalItems(0);
        setTotalPages(0);
        return;
      }

      // Transform dashboard API data into component format
      const transformedData: DashboardProduct[] = response.items.map((item: any) => {
        // Format price function
        const formatPrice = (price: number | undefined): string => {
          if (!price || price === 0) return '$0.00';
          return `$${price.toFixed(2)}`;
        };

        return {
          id: item.id,
          code: item.sku_code || '',
          image_path: item.image_path,
          description: item.description || '',
          styleCode: item.style_code || '',
          rrp: formatPrice(item.rrp_ex_gst),
          bwaBuyPrice: formatPrice(item.bwa_buy_price_50_ex_gst),
          regionalRetailAUD: formatPrice(item.price_list?.['1']), // Price List ID 1
          regionalTradeAUD: formatPrice(item.price_list?.['2']), // Price List ID 2
          metroRetailAUD: formatPrice(item.price_list?.['3']), // Price List ID 3
          metroTradeAUD: formatPrice(item.price_list?.['4']), // Price List ID 4
          regionalRetailVIP: formatPrice(item.price_list?.['5']), // Price List ID 5
          regionalTradeVIP1: formatPrice(item.price_list?.['6']), // Price List ID 6
          regionalTradeVIP2: formatPrice(item.price_list?.['7']), // Price List ID 7
          metroRetailVIP: formatPrice(item.price_list?.['8']), // Price List ID 8
          metroTradeVIP1: formatPrice(item.price_list?.['9']), // Price List ID 9
          metroTradeVIP2: formatPrice(item.price_list?.['10']), // Price List ID 10
          supplierName: item.supplier_name || '',
          supplierId: item.supplier_id
        };
      });

      console.log('SuppliersDashboard - Transformed data:', transformedData.length, 'products');

      setDashboardData(transformedData);
      setTotalItems(response.total || 0);
      setTotalPages(response.pages || Math.ceil((response.total || 0) / itemsPerPage));
      
    } catch (error) {
      console.error('SuppliersDashboard - Error fetching data:', error);
      toast.showToast('Failed to load dashboard data', { type: 'error' });
      setDashboardData([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
      apiCallInProgress.current = false;
    }
  }, [currentPage, itemsPerPage, searchQuery, toast]);

  // Effect to sync URL parameters with state on mount and URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get('search') || '';
    const urlPage = parseInt(params.get('page') || '1', 10);
    const urlLimit = parseInt(params.get('limit') || '5', 10);

    // Only update state if URL params differ from current state (prevents unnecessary updates)
    if (urlSearch !== searchQuery) {
      setSearchQuery(urlSearch);
    }

    if (urlPage !== currentPage || urlLimit !== itemsPerPage) {
      setPagination({ currentPage: urlPage, itemsPerPage: urlLimit });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]); // Only depend on location.search to avoid loops - state vars are intentionally excluded

  // Effect for fetching data - triggers when pagination or search changes
  useEffect(() => {
    if (apiCallInProgress.current) return;

    // Use requestAnimationFrame to batch state updates
    const rafId = requestAnimationFrame(() => {
      fetchDashboardData();
    });

    return () => cancelAnimationFrame(rafId);
  }, [fetchDashboardData]);

  // Initial mount effect - ensure data is fetched on first load
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      // Trigger initial fetch after a brief delay to ensure state is initialized
      const timer = setTimeout(() => {
        if (!apiCallInProgress.current) {
          fetchDashboardData();
        }
      }, 0);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Effect for search query changes - debounce URL updates
  useEffect(() => {
    // Skip on initial mount if no search query
    if (!hasInitializedSearch.current && !searchQuery) {
      hasInitializedSearch.current = true;
      return;
    }

    hasInitializedSearch.current = true;

    // Debounce URL updates when user types
    const timer = setTimeout(() => {
      // Reset to page 1 when search changes
      if (currentPage !== 1) {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
      }
      updateURLParams(1, itemsPerPage, searchQuery);
    }, 300); // Increased debounce for better UX

    return () => clearTimeout(timer);
  }, [searchQuery, itemsPerPage, updateURLParams]);

  // Pagination handlers
  const handlePageChange = (pageNumber: number) => {
    setPagination(prev => ({ ...prev, currentPage: pageNumber }));
    updateURLParams(pageNumber, itemsPerPage, searchQuery);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setPagination(prev => ({ ...prev, currentPage: 1, itemsPerPage: newItemsPerPage }));
    updateURLParams(1, newItemsPerPage, searchQuery);
  };

  // Search handler
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
  };

  // Navigation handlers
  const handleBackClick = () => {
    navigate('/suppliers');
  };

  const handleExport = () => {
    if (dashboardData.length === 0) {
      toast.showToast('No data to export', { type: 'warning' });
      return;
    }

    try {
    // Create CSV content
      const headers = [
        'SKU Code',
        'Supplier',
        'Product Description',
        'Style Code',
        'RRP (ex GST)',
        'BWA BUY PRICE 50% (ex GST)',
        'Regional Retail AUD Incl',
        'Regional Trade AUD Incl',
        'Metro Retail AUD Incl',
        'Metro Trade AUD Incl',
        'Regional Retail VIP AUD Incl',
        'Regional Trade VIP1 AUD Incl',
        'Regional Trade VIP2 AUD Incl',
        'Metro Retail VIP AUD Incl',
        'Metro Trade VIP1 AUD Incl',
        'Metro Trade VIP2 AUD Incl'
      ];
      
    const csvContent = [
      headers.join(','),
        ...dashboardData.map(item => [
        item.code,
          `"${item.supplierName}"`,
        `"${item.description}"`,
        item.styleCode,
        item.rrp,
        item.bwaBuyPrice,
        item.regionalRetailAUD,
        item.regionalTradeAUD,
          item.metroRetailAUD,
          item.metroTradeAUD || '$0.00',
          item.regionalRetailVIP || '$0.00',
          item.regionalTradeVIP1 || '$0.00',
          item.regionalTradeVIP2 || '$0.00',
          item.metroRetailVIP || '$0.00',
          item.metroTradeVIP1 || '$0.00',
          item.metroTradeVIP2 || '$0.00'
      ].join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `suppliers-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

      toast.showToast('Dashboard data exported successfully', { type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      toast.showToast('Failed to export data', { type: 'error' });
    }
  };

  return (
    <Layout>
      {/* Toast notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {}}
        />
      )}

      <PageContainer>
        <PageHeader>
          <BackButton onClick={handleBackClick}>
            <BackIcon src={backButtonIcon} alt="Back" />
          </BackButton>
          <PageTitle>Suppliers Dashboard</PageTitle>
        </PageHeader>

        <ControlsContainer>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search by SKU, description, style, or supplier..."
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={loading}
            />
          </SearchContainer>
          <StatsContainer>
            <StatItem>
              Showing <strong>{dashboardData.length}</strong> of <strong>{totalItems}</strong> products
            </StatItem>
            <ExportButton onClick={handleExport} disabled={loading || dashboardData.length === 0}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </ExportButton>
          </StatsContainer>
        </ControlsContainer>

        <TableContainer>
          <ScrollableWrapper>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>SKU CODE</TableHeaderCell>
                  <TableHeaderCell>SKU Image</TableHeaderCell>
                  <TableHeaderCell>Product Description</TableHeaderCell>
                  <TableHeaderCell>Style Code</TableHeaderCell>
                  <TableHeaderCell>Supplier</TableHeaderCell>
                  <TableHeaderCell>RRP<br />(ex GST)</TableHeaderCell>
                  <TableHeaderCell>BWA BUY<br />PRICE 50%<br />(ex GST)</TableHeaderCell>
                  <TableHeaderCell>Regional<br />Retail AUD<br />(ex GST)</TableHeaderCell>
                  <TableHeaderCell>Regional<br />Trade AUD<br />(ex GST)</TableHeaderCell>
                  <TableHeaderCell>Metro<br />Retail AUD<br />(ex GST)</TableHeaderCell>
                  <TableHeaderCell>Metro<br />Trade AUD<br />(ex GST)</TableHeaderCell>
                  <TableHeaderCell>Regional<br />Retail VIP<br />AUD (ex GST)</TableHeaderCell>
                  <TableHeaderCell>Regional<br />Trade VIP1<br />AUD (ex GST)</TableHeaderCell>
                  <TableHeaderCell>Regional<br />Trade VIP2<br />AUD (ex GST)</TableHeaderCell>
                  <TableHeaderCell>Metro<br />Retail VIP<br />AUD (ex GST)</TableHeaderCell>
                  <TableHeaderCell>Metro<br />Trade VIP1<br />AUD (ex GST)</TableHeaderCell>
                  <TableHeaderCell>Metro<br />Trade VIP2<br />AUD (ex GST)</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={17} style={{ padding: 0 }}>
                      <LoadingContainer>
                        <LoadingSpinner size="lg" />
                        <LoadingText>Loading dashboard data...</LoadingText>
                      </LoadingContainer>
                    </TableCell>
                  </TableRow>
                ) : dashboardData.length > 0 ? (
                  dashboardData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.code}</TableCell>
                      <TableCell>
                        <SkuImage
                          imagePath={item.image_path}
                          alt="SKU Image"
                          width={40}
                          height={40}
                          enableModal={true}
                          showBlankWhenNoImage={true}
                        />
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.styleCode}</TableCell>
                      <TableCell>{item.supplierName}</TableCell>
                      <PriceCell className="rrp">{item.rrp}</PriceCell>
                      <PriceCell className="buy-price">{item.bwaBuyPrice}</PriceCell>
                      <PriceCell className="retail">{item.regionalRetailAUD}</PriceCell>
                      <PriceCell className="trade">{item.regionalTradeAUD}</PriceCell>
                      <PriceCell className="retail">{item.metroRetailAUD}</PriceCell>
                      <PriceCell className="trade">{item.metroTradeAUD}</PriceCell>
                      <PriceCell className="retail">{item.regionalRetailVIP}</PriceCell>
                      <PriceCell className="trade">{item.regionalTradeVIP1}</PriceCell>
                      <PriceCell className="trade">{item.regionalTradeVIP2}</PriceCell>
                      <PriceCell className="retail">{item.metroRetailVIP}</PriceCell>
                      <PriceCell className="trade">{item.metroTradeVIP1}</PriceCell>
                      <PriceCell className="trade">{item.metroTradeVIP2}</PriceCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={17}>
                      <EmptyState>
                        <h3>No products found</h3>
                        <p>
                          {searchQuery 
                            ? 'Try adjusting your search criteria or clear the search to see all products.' 
                            : 'No supplier products available. Please check your suppliers have price lists uploaded.'
                          }
                        </p>
                      </EmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollableWrapper>
          <ScrollHint>
            Scroll horizontally to view all columns
          </ScrollHint>
        </TableContainer>

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <PaginationWrapper>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[5, 10, 20, 50, 100]}
              showItemsPerPage={true}
            />
          </PaginationWrapper>
        )}
      </PageContainer>
    </Layout>
  );
};

export default SuppliersDashboard; 